import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Globe } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { t } from '../translations';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const { language, toggleLanguage } = useLanguage();

  const isActive = (path) => location.pathname === path;

  const navLinks = [
    { path: '/', labelKey: 'nav.home' },
    { path: '/about', labelKey: 'nav.about' },
    { path: '/programs', labelKey: 'nav.programs' },
    { path: '/media', labelKey: 'nav.stories' },
    // TEMPORARILY HIDDEN - Coffee page hidden per user request, restore when ready
    // { path: '/coffee', labelKey: 'nav.coffee' },
    { path: '/heros', labelKey: 'nav.heroes' },
    { path: '/get-involved', labelKey: 'nav.getInvolved' }
  ];

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md shadow-sm" data-testid="main-navbar">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-14 md:h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            <img 
              src="https://customer-assets.emergentagent.com/job_7470f516-1d16-4323-b125-48fedd8be4ef/artifacts/3qpodzn8_logo%20rescue.jpg" 
              alt="The Safe House Logo" 
              className="w-16 h-16 md:w-20 md:h-20 object-contain rounded-lg"
            />
            <div className="flex flex-col -space-y-1">
              <div className="text-base md:text-lg font-bold text-gray-900 text-center">The Safe House</div>
              <div className="text-xs md:text-sm text-gray-600 text-center">Children's Rescue Center</div>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-sm font-medium transition-colors ${
                  isActive(link.path)
                    ? 'text-sh-primary'
                    : 'text-gray-700 hover:text-sh-primary'
                }`}
                data-testid={`nav-link-${link.path.replace('/', '') || 'home'}`}
              >
                {t(link.labelKey, language)}
              </Link>
            ))}
            
            {/* Language Toggle - Desktop */}
            <button
              onClick={toggleLanguage}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 border border-gray-300 rounded-full hover:border-gray-400 transition-all"
              data-testid="language-toggle-desktop"
            >
              <Globe className="w-4 h-4" />
              <span>{language === 'en' ? 'ES' : 'EN'}</span>
            </button>
            
            <Link
              to="/donate"
              className="px-6 py-2.5 bg-gradient-to-r from-sh-primary to-sh-bright text-white rounded-full font-medium hover:shadow-lg transition-all duration-300 transform hover:scale-105"
              data-testid="donate-button-nav"
            >
              {t('nav.donateNow', language)}
            </Link>
          </div>

          {/* Mobile - Menu Button + Language Toggle */}
          <div className="lg:hidden flex items-center">
            {/* Menu Button */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors flex flex-col items-center leading-none"
              data-testid="mobile-menu-button"
            >
              {isOpen ? <X size={22} /> : <Menu size={22} />}
              <span className="text-[10px] uppercase tracking-wide text-gray-900 mt-0.5">{t('nav.menu', language)}</span>
            </button>

            {/* Language Toggle - Mobile */}
            <button
              onClick={toggleLanguage}
              className="flex flex-col items-center ml-3 px-1 py-0.5 text-[10px] text-gray-600 leading-tight"
              data-testid="language-toggle-mobile"
            >
              <Globe className="w-2.5 h-2.5 mb-px" />
              <span>{language === 'en' ? 'En' : 'In'}</span>
              <span>{language === 'en' ? 'Español' : 'English'}</span>
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="lg:hidden py-4 border-t border-gray-200" data-testid="mobile-menu">
            <div className="flex flex-col space-y-4">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsOpen(false)}
                  className={`px-4 py-2 rounded-lg transition-colors ${
                    isActive(link.path)
                      ? 'bg-sh-primary/10 text-sh-primary font-medium'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                  data-testid={`mobile-nav-link-${link.path.replace('/', '') || 'home'}`}
                >
                  {t(link.labelKey, language)}
                </Link>
              ))}
              <Link
                to="/donate"
                onClick={() => setIsOpen(false)}
                className="mx-4 px-6 py-3 bg-gradient-to-r from-sh-primary to-sh-bright text-white rounded-full font-medium text-center"
                data-testid="donate-button-mobile"
              >
                {t('nav.donateNow', language)}
              </Link>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;